import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { loadStripe } from 'src/models/cart/utils';
import { fetchSetupIntent } from 'src/models/payment/payment';
import { getUserDisplayName } from 'src/models/user/utils';

import { Button } from './Button';

import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import type { User } from 'src/models/user/_types';

interface BankAccountFormProps {
  user?: User;
  onPaymentMethodAdded: (result: any) => void;
  onCancel?: () => void;
  disabled?: boolean;
  existingPaymentMethod?: string;
}

export const BankAccountForm: React.FC<BankAccountFormProps> = ({
  user,
  onPaymentMethodAdded,
  onCancel,
  disabled = false,
  existingPaymentMethod,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'bankAccountForm' });
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { session } = useSession();

  useEffect(() => {
    if (session?.stripeToken) {
      setStripePromise(loadStripe(session));
    }
  }, [session]);

  useEffect(() => {
    if (
      !user ||
      !stripePromise ||
      clientSecret ||
      disabled ||
      existingPaymentMethod
    ) {
      return;
    }

    // Function to fetch the client secret for bank account only
    const fetchClientSecret = async () => {
      fetchSetupIntent({
        userId: user.id,
        paymentMethodTypes: ['us_bank_account'], // Only allow bank accounts
      }).then((data) => {
        setClientSecret(data.clientSecret);
      });
    };

    fetchClientSecret();
  }, [user, stripePromise, clientSecret, disabled, existingPaymentMethod]);

  // Show existing payment method info if available
  if (existingPaymentMethod) {
    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('existingBankAccount')}
        </Typography>
        <Typography variant="body1">{existingPaymentMethod}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('bankAccountSetNote')}
        </Typography>
      </Stack>
    );
  }

  if (disabled) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('bankAccountRequired')}
      </Typography>
    );
  }

  if (!clientSecret) {
    return <Typography variant="body2">{t('loading')}</Typography>;
  }

  const options = {
    clientSecret,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <BankAccountCheckoutForm
        user={user}
        onPaymentMethodAdded={onPaymentMethodAdded}
        onCancel={onCancel}
      />
    </Elements>
  );
};

interface BankAccountCheckoutFormProps {
  user?: User;
  onPaymentMethodAdded: (result: any) => void;
  onCancel?: () => void;
}

const BankAccountCheckoutForm: React.FC<BankAccountCheckoutFormProps> = ({
  user,
  onPaymentMethodAdded,
  onCancel,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'bankAccountForm' });
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: getUserDisplayName(user),
            email: user?.email,
          },
        },
        expand: ['payment_method'],
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    setIsSubmitting(false);

    if (result.error) {
      // Error handling could be enhanced here
    } else {
      onPaymentMethodAdded(result);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        name: 'never',
        email: 'never',
      },
    },
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
    // Restrict to bank account types only (handled by setup intent)
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('bankAccountOnly')}
        </Typography>
        <PaymentElement options={paymentElementOptions} />
        <Stack direction="row" spacing={2}>
          {onCancel && (
            <Button
              variant="bordered"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            variant="prominent"
            disabled={!stripe || isSubmitting}
            type="submit"
          >
            {isSubmitting ? t('processing') : t('addBankAccount')}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
